import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { fetchOrganizationEstablishmentsQueryOptions } from "@/api/establishments";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import { PageHeader } from "@/components/PageHeader";
import { useApiParameters } from "@/hooks/useApiParameters";
import { ScheduleForm } from "./-components/ScheduleForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/schedules/new",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Preload establishments and staff data
		context.queryClient.ensureQueryData(
			fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchOrganizationStaffQueryOptions(token, orgId),
		);

		return { userId, token, orgId };
	},
	component: NewSchedulePage,
});

function NewSchedulePage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/schedules/new",
	});
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Get establishments and staff data
	const { data: establishments } = useSuspenseQuery(
		fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
	);
	const { data: staff } = useSuspenseQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);

	// Handle successful form submission
	const handleSuccess = () => {
		navigate({
			to: "/app/org/$orgId/schedules",
			params: { orgId },
		});
	};

	return (
		<div className="h-full flex flex-col">
			<PageHeader
				title={t("schedules.form.create.title")}
				description={t("schedules.form.create.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
			/>

			<div className="flex-1 min-h-0 overflow-hidden">
				<ScheduleForm
					orgId={orgId}
					token={token}
					establishments={establishments}
					staff={staff}
					onSuccess={handleSuccess}
				/>
			</div>
		</div>
	);
}
